import { TRASH, bulkOf, type ArenaAnchor, type GameSeed, type PhaseSpec, type ReceiverTier, type SpawnSpec, type TicketSpec, type TrashKind } from "./types.js";

const PHASE_LABELS = [
  "Safe routing",
  "Multi-chute batching",
  "Technical routing",
  "Red-orb pressure",
  "Mixed routes and shutters",
  "Final surge",
] as const;

const ANCHORS: ArenaAnchor[][] = [
  ["floor", "wall", "ramp"],
  ["floor", "ramp", "island"],
  ["floor", "wall", "precision"],
  ["floor", "island", "rail"],
  ["floor", "precision", "rail"],
  ["floor", "wall", "ramp", "island", "precision", "rail"],
];

/**
 * Locked pressure curve (LOCKED-DESIGN Section 10, conformed per 18.7):
 * average inbound pressure escalates from ~0.8 bulk/second in phase one to
 * ~2.2 bulk/second in the final surge. Each 30-second phase spawns exactly
 * this many bulk units, giving per-phase rates of
 * 0.80 / 1.07 / 1.37 / 1.63 / 1.93 / 2.20 bulk/second (all within +/-15% of
 * the linear 0.8 -> 2.2 ramp). Ticket-guaranteed supply is part of the
 * budget, not stacked on top of it.
 */
const PHASE_BULK_BUDGET = [24, 32, 41, 49, 58, 66] as const;
/** Telegraphed three-to-six-bulk mini-batches per phase (Section 10 grammar). */
const PHASE_BATCH_COUNTS = [2, 2, 3, 3, 3, 4] as const;
/** Bulk always reserved for the steady baseline feed of a phase. */
const BASELINE_MIN_BULK = 6;
/** Phase-relative spawn windows (ticks). Baseline stays inside every ticket
 * supply window [240, 1620) so guaranteed pieces always count. */
const BASELINE_WINDOW = { start: 240, end: 1560 } as const;
const BATCH_WINDOW = { start: 420, end: 1380 } as const;
const BATCH_PIECE_SPACING = 12;

type SpawnProfile = "baseline" | "recovery" | "routes" | "simple";
type SpawnKind = Exclude<TrashKind, "husk">;

/** Filler composition character per curated seed (crate / can / green). */
const PROFILE_MIX: Record<SpawnProfile, { crate: number; can: number }> = {
  baseline: { crate: 0.55, can: 0.3 },
  recovery: { crate: 0.5, can: 0.3 },
  routes: { crate: 0.4, can: 0.35 },
  // Simple default; each Simple seed passes an explicit mix (SIMPLE_SEEDS below)
  // whose crate+can === 1.0 so drawPieces can NEVER roll green (18.11).
  simple: { crate: 0.7, can: 0.3 },
};

/**
 * Simple-mode seed generation overrides (LOCKED-DESIGN 18.11). The three
 * official seeds use the locked PHASE_BULK_BUDGET / PHASE_BATCH_COUNTS globals
 * and their profile mix; Simple seeds pass gentler per-phase budgets, fewer
 * telegraphed mini-batches, and a green-free draw mix. Purely additive — the
 * official code paths default to OFFICIAL_CONFIG and are byte-identical.
 */
interface SeedGenConfig {
  budgets: readonly number[];
  batchCounts: readonly number[];
  mix?: { crate: number; can: number };
  /** Simple v2 (18.11): presentation ruleset carried on the seed. */
  beamCapacity?: number;
  portalCycle?: readonly ArenaAnchor[];
}
const OFFICIAL_CONFIG: SeedGenConfig = { budgets: PHASE_BULK_BUDGET, batchCounts: PHASE_BATCH_COUNTS };

/** Early-reader phase labels for Simple seeds; hidden in the Simple HUD but harmless if surfaced. */
const SIMPLE_PHASE_LABELS = ["Warm up", "Keep going", "Nice work", "More trash", "Lots of trash", "Big finish"] as const;

/**
 * Simple-mode phases (LOCKED-DESIGN 18.11 + Simple v2 amendment). Each phase
 * exposes the safe "floor" anchor, the phase's own portal-cycle anchor, AND the
 * NEXT phase's cycle anchor. The kernel placement gate requires a target be
 * active in the phase it is ISSUED in, and the auto-move pre-arms the swap ~45
 * ticks (0.75s) before the phase boundary so the atomic swap lands exactly on
 * the boundary — that pre-arm happens while the PRIOR phase is still current, so
 * the prior phase must already list the upcoming anchor as legal. Including the
 * next anchor also keeps the boundary fallback from firing (the just-swapped
 * anchor stays legal in the new phase). "floor" stays legal per 3.1 as the
 * always-available safe option; receivers stay safe+technical, belt forward, no
 * closed lane — escalation is RATE + reach, never hazard mechanics. cycle[0] is
 * always "floor" so the fresh sim's default route already matches phase 1.
 */
function simplePhases(portalCycle: readonly ArenaAnchor[]): PhaseSpec[] {
  return SIMPLE_PHASE_LABELS.map((label, index): PhaseSpec => {
    const current = portalCycle[index] ?? "floor";
    const next = portalCycle[index + 1];
    const activeAnchors: ArenaAnchor[] = ["floor"];
    for (const anchor of [current, ...(next ? [next] : [])]) {
      if (!activeAnchors.includes(anchor)) activeAnchors.push(anchor);
    }
    return {
      index: index + 1,
      label,
      startsAt: index * 30 * 60,
      activeAnchors,
      activeReceivers: ["safe", "technical"],
      conveyor: "forward",
    };
  });
}

function phases(routeHeavy = false): PhaseSpec[] {
  return PHASE_LABELS.map((label, index) => {
    const activeReceivers: ReceiverTier[] = index === 5
      ? ["safe", "technical", "hazard"]
      : index < 2
        ? ["safe", routeHeavy ? "hazard" : "technical"]
        : ["safe", index % 2 === 0 ? "technical" : "hazard"];
    return {
      index: index + 1,
      label,
      startsAt: index * 30 * 60,
      activeAnchors: ANCHORS[index]!,
      activeReceivers,
      conveyor: index % 2 === 0 ? "forward" : "reverse",
      ...(index >= 4 ? { closedLane: index % 2 === 0 ? "left" as const : "right" as const } : {}),
    };
  });
}

class Rng {
  constructor(private state: number) {}
  next(): number {
    let x = this.state | 0;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x | 0;
    return (x >>> 0) / 4294967296;
  }
}

/** Draws pieces totalling exactly `bulk` using the profile mix (or an override). */
function drawPieces(rng: Rng, profile: SpawnProfile, bulk: number, minPieces = 1, mixOverride?: { crate: number; can: number }): SpawnKind[] {
  const mix = mixOverride ?? PROFILE_MIX[profile];
  const pieces: SpawnKind[] = [];
  let left = bulk;
  while (left > 0) {
    const roll = rng.next();
    let kind: SpawnKind = roll < mix.crate ? "crate" : roll < mix.crate + mix.can ? "can" : "green";
    const piecesStillNeeded = Math.max(0, minPieces - pieces.length - 1);
    const cap = left - piecesStillNeeded;
    while (TRASH[kind].bulk > cap) kind = kind === "green" ? "can" : "crate";
    pieces.push(kind);
    left -= TRASH[kind].bulk;
  }
  return pieces;
}

function shuffle<T>(rng: Rng, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

/** Ticket-guaranteed supply: recipe requirement plus recovery headroom. */
function ticketSupplyPieces(ticket: TicketSpec | undefined): SpawnKind[] {
  if (!ticket) return [];
  return Object.entries(ticket.recipe).flatMap(([kind, required]) => {
    const need = required ?? 0;
    return Array.from({ length: need + Math.ceil(need / 2) }, () => kind as SpawnKind);
  });
}

function buildSpawns(seedValue: number, profile: SpawnProfile, tickets: TicketSpec[], config: SeedGenConfig = OFFICIAL_CONFIG): SpawnSpec[] {
  const rng = new Rng(seedValue);
  const chutes = ["left", "center", "right"] as const;
  const spawns: SpawnSpec[] = [];
  for (let phase = 0; phase < 6; phase++) {
    const phaseStart = phase * 1800;
    let remaining: number = config.budgets[phase]!;

    // Ticket-guaranteed supply rides the baseline feed inside the budget.
    const ticketPieces = ticketSupplyPieces(tickets.find((ticket) => ticket.phase === phase + 1));
    remaining -= bulkOf(ticketPieces);

    // Red-orb flavor from phase four on; the recovery seed is red-heavy.
    // The two-unexploded-orb cap is enforced by the simulation via deferral.
    // Simple seeds skip the injection entirely (18.11: no reds ever), so the
    // red-orb first-encounter can never fire — asserted in validation.
    const flavorReds: SpawnKind[] = profile !== "simple" && phase >= 3
      ? Array.from({ length: profile === "recovery" ? 2 : 1 }, () => "red" as const)
      : [];
    remaining -= bulkOf(flavorReds);

    // Telegraphed mini-batches: 3-6 bulk, always at least two pieces.
    const batchCount = config.batchCounts[phase]!;
    const batches: SpawnKind[][] = [];
    for (let b = 0; b < batchCount; b++) {
      const reservedForRest = (batchCount - b - 1) * 3 + BASELINE_MIN_BULK;
      const batchBulk = Math.max(3, Math.min(6, remaining - reservedForRest, 3 + Math.floor(rng.next() * 4)));
      batches.push(drawPieces(rng, profile, batchBulk, 2, config.mix));
      remaining -= batchBulk;
    }

    // Steady baseline feed fills the rest of the budget exactly.
    const filler = drawPieces(rng, profile, remaining, 1, config.mix);

    // Schedule batches first so the baseline can keep clear of them.
    const batchTicks: number[] = [];
    const span = BATCH_WINDOW.end - BATCH_WINDOW.start;
    batches.forEach((pieces, b) => {
      const center = BATCH_WINDOW.start + Math.round(span * (b + 0.5) / batchCount) + Math.floor(rng.next() * 97) - 48;
      const chute = chutes[Math.floor(rng.next() * 3)]!;
      pieces.forEach((kind, i) => {
        const tick = center + i * BATCH_PIECE_SPACING;
        batchTicks.push(tick);
        spawns.push({ tick: phaseStart + tick, chute, kind });
      });
    });

    // Baseline: even spacing with light jitter, nudged off batch clusters.
    const baselinePieces = shuffle(rng, [...ticketPieces, ...flavorReds, ...filler]);
    const stride = (BASELINE_WINDOW.end - BASELINE_WINDOW.start) / Math.max(1, baselinePieces.length - 1);
    baselinePieces.forEach((kind, i) => {
      let tick = BASELINE_WINDOW.start + Math.round(i * stride) + Math.floor(rng.next() * 21) - 10;
      tick = Math.max(BASELINE_WINDOW.start, Math.min(BASELINE_WINDOW.end, tick));
      const nudge = tick > (BASELINE_WINDOW.start + BASELINE_WINDOW.end) / 2 ? -42 : 42;
      while (batchTicks.some((batchTick) => Math.abs(tick - batchTick) <= 30)) tick += nudge;
      spawns.push({ tick: phaseStart + tick, chute: chutes[Math.floor(rng.next() * 3)]!, kind });
    });
  }
  return spawns.sort((a, b) => a.tick - b.tick || a.chute.localeCompare(b.chute));
}

function buildTickets(profile: SpawnProfile): TicketSpec[] {
  return Array.from({ length: 6 }, (_, i) => {
    const startsAt = i * 1800 + 240;
    const receiver: ReceiverTier | undefined = profile === "routes" && i >= 2
      ? (i % 2 === 0 ? "technical" : "hazard")
      : i >= 4 ? "technical" : undefined;
    const recipe = profile === "recovery" && i >= 3
      ? { red: 3 }
      : i % 3 === 0 ? { crate: 8 } : i % 3 === 1 ? { can: 4 } : { green: 3 };
    return { id: `ticket-${i + 1}`, phase: i + 1, startsAt, expiresAt: startsAt + 1200, recipe, ...(receiver ? { receiver } : {}) };
  });
}

function seed(id: string, title: string, description: string, rngSeed: number, profile: SpawnProfile): GameSeed {
  const tickets = buildTickets(profile);
  return { format: 1, id, version: "2", title, description, rngSeed, phases: phases(profile === "routes"), spawns: buildSpawns(rngSeed, profile, tickets), tickets };
}

/**
 * Simple-mode seed builder (LOCKED-DESIGN 18.11): the shared generator with the
 * "simple" profile, zero tickets, flat Simple phases, and the per-seed gentle
 * budget/batch/mix overrides. Tagged `ruleset: "simple"` so validateSeed applies
 * the honest Simple band instead of the ranked pressure gate.
 */
function simpleSeed(id: string, title: string, description: string, rngSeed: number, config: SeedGenConfig): GameSeed {
  const tickets: TicketSpec[] = [];
  const portalCycle = config.portalCycle ?? ["floor", "ramp", "island", "ramp", "island", "floor"];
  return {
    format: 1,
    id,
    version: "2",
    title,
    description,
    rngSeed,
    phases: simplePhases(portalCycle),
    spawns: buildSpawns(rngSeed, "simple", tickets, config),
    tickets,
    ruleset: "simple",
    rules: {
      beamCapacity: config.beamCapacity ?? 6,
      portalCycle,
      // Gentler jam-recovery windows for kids (LOCKED-DESIGN 18.12): 12s dwell /
      // 40s overload. KERNEL-READ (the sanctioned m1.3.0 exception); official
      // seeds omit rules and fall back to the 9s/30s standard constants.
      dwellTicks: 720,
      overloadTicks: 2400,
    },
  };
}

export const CURATED_SEEDS = [
  seed("baseline-routing", "Clean Lines", "Readable batches and forgiving safe-route pressure.", 0x1a2b3c4d, "baseline"),
  seed("red-recovery", "Hot Return", "Armed red-orb pressure with frequent recovery decisions.", 0x5e6f7788, "recovery"),
  seed("route-receiver", "Switchyard", "Fast anchor and receiver rotation rewards route fluency.", 0x3344aa55, "routes"),
] as const satisfies readonly GameSeed[];

/**
 * Unranked kid-mode seeds (LOCKED-DESIGN 18.11 Simple v2, retuned for the 56-wide
 * arena per LOCKED-DESIGN 18.12 / PLAN-ARENA-JAM-REDESIGN §D5). Deliberately NOT
 * part of CURATED_SEEDS so validate:seeds and export-test-vectors keep iterating
 * only the three official ranked seeds (whose 0.8->2.2 curve is untouched — the
 * no-version-bump guarantee). Doubling the arena doubles the gather trips, so the
 * Simple peaks come down ~20-30%: First Shift 0.50 -> 0.93, Busy Day 0.47 -> 1.13
 * bulk/s, both non-decreasing inside the honest band [0.40, 1.60] — so a competent
 * kid is "barely keeping up by phase 4+, panic not hopeless" even with the longer
 * runs. Busy Day's first two phases are held under the 36-bulk FACILITY_CAPACITY
 * (sum 32) so a kid who falls behind reaches the tower phase before overflowing. The crate+can===1.0 mixes guarantee green never spawns. beamCapacity 6
 * (LOCKED-DESIGN 18.15 / PLAYTEST F1: the standard beam, restored — 18.11 v2's
 * 3-bulk "smaller scoops" is SUPERSEDED because "6 blocks or 3 cylinders" is
 * exactly 6 bulk in the existing table) and the escalating-reach portalCycle are
 * presentation ruleset data the kernel never reads (Engine/GrabBeam consume them;
 * official seeds omit `rules`). It stays EXPLICIT on Simple seeds even though it
 * now equals DEFAULT_BEAM_CAPACITY, so the intent stays recorded. The spawn
 * schedule below is UNCHANGED by F1 — only trips-per-shift (roughly halved)
 * moves, which is a feel input, not a pressure-band input.
 * portalCycle hops are bounded by the 8 s Simple TELEGRAPH, not by a fixed
 * distance: 8 s x maxSpeed 8.5 m/s = 68 m of walking, so a kid who starts moving
 * when the banner lights always arrives. The old "~15 m" claim here was never
 * true of the 18.13 anchor spread (ramp->island is 21.53 m) and had no test
 * behind it, which is why the drift went unnoticed. The enforced bound is now in
 * tests/simple-seeds.test.ts: every consecutive hop <= 34 m (50 % of the
 * telegraph reach) and every hop touching the `floor` entrance <= 21 m, measured
 * against the authored anchor positions in arena.v1.json — and, since 18.25, at
 * BOTH SWEEP EXTREMES for an anchor carrying an `x` motion, because the moving
 * rail panel is almost never at the authored centre the bound used to be
 * measured from. Per-seed hop distances
 * are noted below and are re-measured whenever an anchor moves.
 */
export const SIMPLE_SEEDS = [
  simpleSeed("simple-first-shift", "First Shift", "Nice and easy.", 0x7c11d001, {
    budgets: [15, 18, 21, 24, 26, 28],
    // Ninth-dogfood finale ease (D6): the "Big finish" is single-anchor floor at
    // peak rate; a kid who fell a beat behind got buried by its bursts and dug an
    // unrecoverable jam. Drop the finale from 3 telegraphed batches to 2 so the
    // same 28-bulk budget arrives in gentler waves (freed bulk moves to the evenly
    // spaced baseline). Budget is UNCHANGED, so the locked non-decreasing bulk/s
    // curve is untouched — only burstiness eases. No official/ranked seed, no
    // fixture impact (Simple seeds export no vectors).
    batchCounts: [1, 1, 2, 2, 3, 2],
    mix: { crate: 0.7, can: 0.3 },
    beamCapacity: 6,
    // Gentle seed: only the near anchors; the finale returns home so the peak
    // rate stays fair (reach never spikes at max pressure). CHASE IT! (18.25)
    // spends the phase-4 slot — previously a second `ramp` visit — on the moving
    // `rail` panel, seated between the two `island` visits because island is the
    // only near anchor legal at BOTH sweep extremes. Hops MEASURED against the
    // authored 18.13 anchors + the 18.17 entrance relocation (PLAYTEST F2/F7),
    // and for `rail` at its worst-case swept position, not its authored centre:
    // floor->ramp 9.34, ramp->island 21.53, island->rail 13.01, rail->island
    // 13.01, island->floor 15.19 m — never touches the tower/wall. cycle[1]
    // stays `ramp`: the orientation ring-move (Engine.updateOrientationMove) and
    // three arena.qa scenarios address it by name.
    portalCycle: ["floor", "ramp", "island", "rail", "island", "floor"],
  }),
  simpleSeed("simple-busy-day", "Busy Day", "More trash!", 0x7c11d002, {
    // Phase-4F review retune (LOCKED-DESIGN 18.12): the old [18,21,24,28,32,36]
    // curve summed 39 bulk across phases 1+2 — already over the fixed 36-bulk
    // FACILITY_CAPACITY — so a kid who fell behind hit critical overflow at the
    // phase-2/3 boundary (~50-68s), which is exactly when the first precision
    // (tower) phase begins, contradicting the "barely keeping up by phase 4+"
    // intent. Flattening phases 1-2 so their sum (32) stays under the cap lets a
    // light sorter reach and experience the tower phase before the panic peaks;
    // the escalation and peak stay honest (0.47 -> 1.13 bulk/s, non-decreasing,
    // inside the [0.40, 1.60] band). No fixture impact: Simple seeds export no
    // vectors and the three official ranked seeds are untouched.
    budgets: [14, 18, 22, 27, 31, 34],
    batchCounts: [2, 2, 3, 3, 4, 4],
    mix: { crate: 0.6, can: 0.4 },
    beamCapacity: 6,
    // Re-ordered for the 56-wide anchor spread (18.12): the old
    // floor,ramp,island,wall,precision,island cycle went island->wall = 29.87 m,
    // the longest hop on the board. wall's nearest neighbour is precision, so it
    // sits between precision visits.
    // CHASE IT! (18.25) re-seats the whole cycle so the busier seed meets the
    // moving ring EARLY (phase 3, while there is still slack to enjoy it) rather
    // than at the peak, and so every authored anchor still gets a visit — the
    // rail displaces nothing, it takes the second `island` slot and the tail
    // shifts right. Rail sits between `island` visits for the same reason as
    // First Shift. Hops MEASURED against the authored 18.13 anchors + the 18.17
    // entrance relocation, rail at its worst-case swept position: floor->island
    // 15.19, island->rail 13.01, rail->island 13.01, island->precision 18.11,
    // precision->wall 14.65 m. Peak-reach (wall, via precision) now lands on the
    // finale, so the max-rate panic is about reach as well as speed.
    portalCycle: ["floor", "island", "rail", "island", "precision", "wall"],
  }),
] as const satisfies readonly GameSeed[];

export function getSeed(id: string | undefined): GameSeed {
  return CURATED_SEEDS.find((candidate) => candidate.id === id)
    ?? SIMPLE_SEEDS.find((candidate) => candidate.id === id)
    ?? CURATED_SEEDS[0];
}
